/**
 * User helper functions
 */

import { getUserProfile } from "utils/enterpriseApi";

/**
 * Fetches the current user's profile from the API and updates
 * org_role, team, and is_applicant in localStorage so downstream
 * components always work with fresh values.
 * On network/API failure the existing localStorage values are preserved.
 */
export const refreshUserProfile = async () => {
  try {
    const profile = await getUserProfile();
    const userInfo = JSON.parse(localStorage.getItem("userInfo"));

    if (!userInfo || !profile?.user) {
      return;
    }

    const { org_role, team, is_applicant } = profile.user;

    if (org_role !== undefined) {
      userInfo.org_role = org_role;
    }

    if (!userInfo.user) {
      userInfo.user = {};
    }

    if (team !== undefined) {
      userInfo.user.team = team;
    }

    if (is_applicant !== undefined) {
      userInfo.user.is_applicant = is_applicant;
    }

    localStorage.setItem("userInfo", JSON.stringify(userInfo));
  } catch (error) {
    console.error("Failed to refresh user profile:", error);
  }
};

/**
 * Check if the current user is an applicant
 * @returns {boolean} True if user is an applicant, false otherwise
 */
export const isUserApplicant = () => {
  try {
    const userInfoStr = localStorage.getItem("userInfo");
    if (!userInfoStr) return false;

    const userInfo = JSON.parse(userInfoStr);
    return userInfo?.user?.is_applicant === true;
  } catch (error) {
    console.error('Error checking if user is applicant:', error);
    return false;
  }
};

/**
 * Get user info from localStorage
 * @returns {object|null} User info object or null if not found
 */
export const getUserInfo = () => {
  try {
    const userInfoStr = localStorage.getItem("userInfo");
    if (!userInfoStr) return null;

    return JSON.parse(userInfoStr);
  } catch (error) {
    console.error('Error getting user info:', error);
    return null;
  }
};

/**
 * Get user object from user info
 * @returns {object|null} User object or null if not found
 */
export const getUser = () => {
  const userInfo = getUserInfo();
  return userInfo?.user || userInfo || null;
};

/**
 * Check if user has a specific role
 * @param {string} role - Role to check (e.g., 'ADMIN', 'QA', 'JUNIOR EDITOR')
 * @returns {boolean} True if user has the role, false otherwise
 */
export const hasUserRole = (role) => {
  const userInfo = getUserInfo();
  return userInfo?.org_role === role;
};

/**
 * Check if user is an admin
 * @returns {boolean} True if user is an admin, false otherwise
 */
export const isUserAdmin = () => {
  return hasUserRole('ADMIN');
};

/**
 * Clean title by removing " live " and " batch "
 * @param {string} title - The title to clean
 * @returns {string} Cleaned title
 */
export const cleanTitle = (title) =>
  title?.replace(/ (live|batch) /gi, " ") || title;
